import { useState, useEffect } from 'react';
import { Download, Trash2, RefreshCw, Users, Mail, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWaitlist = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist/all`);
      const data = await res.json();
      if (data.success) {
        setEntries(data.entries);
        setTotal(data.total);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchEntries(); }, []);

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/waitlist/${encodeURIComponent(email)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setEntries(prev => prev.filter(e => e.email !== email));
        setTotal(prev => prev - 1);
      }
    } catch { /* ignore */ }
  };

  const exportCSV = () => {
    const header = 'Email,Signed Up At\n';
    const rows = entries.map(e => `${e.email},${e.signed_up_at}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `6thmate_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-mate-sand font-body" data-testid="admin-waitlist-page">
      {/* Header */}
      <div className="bg-mate-green">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-base">6</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">6th Mate</span>
            <span className="text-white/50 mx-2">/</span>
            <span className="text-white/80 text-sm font-medium">Admin</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-3xl text-white mb-1">Waitlist</h1>
              <p className="text-white/70 text-base">Manage your early mentor signups</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 flex items-center gap-3" data-testid="waitlist-total-badge">
                <Users size={20} className="text-mate-terracotta" />
                <div>
                  <p className="text-2xl font-bold text-white leading-none">{total}</p>
                  <p className="text-white/60 text-xs mt-0.5">total signups</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between border-b border-mate-mist">
        <p className="text-gray-500 text-sm">
          {total} {total === 1 ? 'entry' : 'entries'}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchEntries}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-mate-green px-3 py-2 rounded-lg hover:bg-mate-green/5 transition-colors"
            data-testid="refresh-btn"
            aria-label="Refresh list"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={exportCSV}
            disabled={entries.length === 0}
            className="flex items-center gap-1.5 text-sm font-semibold text-white bg-mate-terracotta px-4 py-2 rounded-full hover:scale-105 transition-transform disabled:opacity-40 disabled:hover:scale-100"
            data-testid="export-csv-btn"
            aria-label="Export as CSV"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-6">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <Users size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-400">No signups yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-mate-mist overflow-hidden">
            <table className="w-full" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-mate-mist bg-gray-50/50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3 w-10">#</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                    <span className="flex items-center gap-1.5"><Mail size={14} /> Email</span>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3 hidden sm:table-cell">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> Signed Up</span>
                  </th>
                  <th className="w-16 px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <tr key={entry.email} className="border-b border-mate-mist/50 last:border-0 hover:bg-mate-sand/50 transition-colors" data-testid={`waitlist-row-${i}`}>
                    <td className="px-6 py-4 text-sm text-gray-400">{i + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{entry.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 hidden sm:table-cell">{formatDate(entry.signed_up_at)}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleDelete(entry.email)}
                        className="text-gray-300 hover:text-red-500 transition-colors p-1"
                        data-testid={`delete-btn-${i}`}
                        aria-label={`Remove ${entry.email}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWaitlist;
